import { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import FormInput from "./FormInput"
import Button from "./Button"
import CityCombobox, { cityLabel } from "./CityCombobox"
import {
  faBuilding,
  faHashtag,
  faLocationDot,
  faLock,
  faMapPin,
  faRoad,
} from "@fortawesome/free-solid-svg-icons"
import { cepDigits, formatCep, lookupCep } from "../utils/viaCep"

export const EMPTY_ADDRESS = {
  postal_code: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
}

// Prefill from GET /v1/users/billing-profile.
export const addressFromProfile = (profile) => ({
  ...EMPTY_ADDRESS,
  street: profile?.address?.street ?? "",
  number: profile?.address?.number ?? "",
  complement: profile?.address?.complement ?? "",
  neighborhood: profile?.address?.neighborhood ?? "",
  city: profile?.address?.city ?? "",
  state: profile?.address?.state ?? "",
  postal_code: profile?.address?.postal_code ?? "",
})

// Mirrors the api: everything but the complement, UF with 2 letters, CEP
// with 8 digits.
export const isAddressComplete = (address) =>
  cepDigits(address.postal_code).length === 8 &&
  /^[A-Za-z]{2}$/.test(address.state.trim()) &&
  ["street", "number", "neighborhood", "city"].every((field) => address[field].trim() !== "")

/**
 * Billing address of the Pix checkout (D8, D71). Typing the 8th CEP digit
 * fills street / neighborhood from ViaCEP and locks city / UF to the CEP's
 * (changing the CEP unlocks them). A CEP ViaCEP does not know — or a city the
 * api rejected (invalidCity bumps) — opens the "Cidade — UF" search over our
 * municipios, so the NFS-e always gets an IBGE code.
 */
export default function AddressForm({ initialAddress, onSubmit, isLoading, submitText, invalidCity = 0 }) {
  const initial = { ...EMPTY_ADDRESS, ...initialAddress }
  const [address, setAddress] = useState({
    postal_code: formatCep(initial.postal_code),
    street: initial.street,
    number: initial.number,
    complement: initial.complement,
    neighborhood: initial.neighborhood,
  })
  const [city, setCity] = useState(
    initial.city && initial.state ? { city: initial.city, state: initial.state.toUpperCase() } : null
  )
  // idle (no lookup yet) | looking | found (city locked) | notFound |
  // rejected (the api refused the city: pick it from the list)
  const [cepState, setCepState] = useState("idle")
  const [cityError, setCityError] = useState(null)
  // Remounts the city search when its starting value changes from outside.
  const [cityKey, setCityKey] = useState(0)
  // The CEP typed last: a slower lookup of an earlier CEP must not fill
  // the fields.
  const currentCep = useRef(cepDigits(address.postal_code))

  const update = (name, value) => setAddress((current) => ({ ...current, [name]: value }))

  const lookup = async (digits, { fillAll }) => {
    setCepState("looking")
    const found = await lookupCep(digits)
    if (currentCep.current !== digits) return
    if (!found || !found.city || !found.state) {
      setCepState("notFound")
      setCityKey((k) => k + 1)
      return
    }
    setAddress((current) => ({
      ...current,
      street: (fillAll || !current.street ? found.street : "") || current.street,
      neighborhood: (fillAll || !current.neighborhood ? found.neighborhood : "") || current.neighborhood,
    }))
    setCity({ city: found.city, state: found.state.toUpperCase() })
    setCityError(null)
    setCepState("found")
  }

  // A prefilled CEP locks its city as well (the prefill keeps the rest).
  useEffect(() => {
    const digits = currentCep.current
    if (digits.length === 8) lookup(digits, { fillAll: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The api answered invalid_city (the counter moved since this form
  // mounted): the member picks the city from the list.
  const seenInvalidCity = useRef(invalidCity)
  useEffect(() => {
    if (invalidCity === seenInvalidCity.current) return
    seenInvalidCity.current = invalidCity
    setCity(null)
    setCepState("rejected")
    setCityError("Escolha a cidade na lista.")
    setCityKey((k) => k + 1)
  }, [invalidCity])

  const onChange = (e) => {
    const { name, value } = e.target
    if (name !== "postal_code") {
      update(name, value)
      return
    }

    const formatted = formatCep(value)
    const digits = cepDigits(formatted)
    const previous = currentCep.current
    currentCep.current = digits
    update("postal_code", formatted)
    if (digits === previous) return

    // Another CEP: the city it locked (or the api refused) no longer applies.
    if (cepState === "found" || cepState === "rejected") {
      setCity(null)
      setCityKey((k) => k + 1)
    }
    if (digits.length !== 8) {
      setCepState("idle")
      return
    }
    lookup(digits, { fillAll: true })
  }

  const fullAddress = { ...address, city: city?.city ?? "", state: city?.state ?? "" }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!isAddressComplete(fullAddress)) return
    onSubmit({
      ...fullAddress,
      postal_code: cepDigits(fullAddress.postal_code),
      state: fullAddress.state.trim().toUpperCase(),
    })
  }

  const inputs = [
    { name: "street", placeholder: "Rua", icon: faRoad, required: true, errorMessage: "Informe a rua", autoComplete: "address-line1" },
    { name: "number", placeholder: "Número", icon: faHashtag, required: true, errorMessage: "Informe o número" },
    { name: "complement", placeholder: "Complemento (opcional)", icon: faBuilding, autoComplete: "address-line2" },
    { name: "neighborhood", placeholder: "Bairro", icon: faLocationDot, required: true, errorMessage: "Informe o bairro" },
  ]

  return (
    <form className="addressForm" onSubmit={handleSubmit} noValidate={false}>
      <FormInput
        id="postal_code"
        name="postal_code"
        placeholder={cepState === "looking" ? "Buscando endereço…" : "CEP"}
        icon={faMapPin}
        inputMode="numeric"
        pattern="\d{5}-?\d{3}"
        required
        errorMessage="Informe um CEP com 8 dígitos"
        autoComplete="postal-code"
        value={address.postal_code}
        onChange={onChange}
      />
      {cepState === "notFound" && (
        <p className="addressForm__notice" role="status">
          Não encontramos esse CEP. Confira os números ou preencha o endereço abaixo.
        </p>
      )}
      {inputs.map((input) => (
        <FormInput key={input.name} id={input.name} {...input} value={address[input.name]} onChange={onChange} />
      ))}
      {cepState === "found" && city ? (
        <div className="addressForm__locked">
          <div className="addressForm__lockedValue" aria-label="Cidade — UF">
            <FontAwesomeIcon icon={faLock} aria-hidden="true" />
            <span>{cityLabel(city.city, city.state)}</span>
          </div>
          <span className="addressForm__hint">Definida pelo CEP. Para mudar, corrija o CEP.</span>
        </div>
      ) : (
        <CityCombobox
          key={cityKey}
          value={city}
          onChange={(choice) => {
            setCity(choice)
            if (choice) setCityError(null)
          }}
          error={cityError}
        />
      )}
      <Button
        type="submit"
        text={submitText}
        isLoading={isLoading}
        disabled={!isAddressComplete(fullAddress) || isLoading}
      />
    </form>
  )
}
